import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';

// Toast types
const ToastType = {
    SUCCESS: 'success',
    ERROR: 'error',
    WARNING: 'warning',
    INFO: 'info',
};

// Toast Context
const ToastContext = createContext(null);

// Toast Component
function Toast({ toast, onRemove }) {
    const [isExiting, setIsExiting] = useState(false);

    const handleRemove = useCallback(() => {
        setIsExiting(true);
        setTimeout(() => onRemove(toast.id), 300);
    }, [toast.id, onRemove]);

    const typeStyles = {
        success: 'bg-green-500 border-green-600',
        error: 'bg-red-500 border-red-600',
        warning: 'bg-yellow-500 border-yellow-600',
        info: 'bg-blue-500 border-blue-600',
    };

    const icons = {
        success: '✓',
        error: '✕',
        warning: '⚠',
        info: 'ℹ',
    };

    return (
        <div
            className={`
                flex items-start gap-3 p-4 rounded-lg shadow-lg border-l-4 text-white
                transform transition-all duration-300 ease-in-out
                ${typeStyles[toast.type] || typeStyles.info}
                ${isExiting ? 'opacity-0 translate-x-full' : 'opacity-100 translate-x-0'}
            `}
            style={{ minWidth: '300px', maxWidth: '400px' }}
        >
            <span className="text-lg font-bold">{icons[toast.type] || icons.info}</span>
            <div className="flex-1">
                {toast.title && (
                    <div className="font-semibold mb-1">{toast.title}</div>
                )}
                <div className="text-sm opacity-95">{toast.message}</div>
            </div>
            <button
                onClick={handleRemove}
                className="text-white/80 hover:text-white transition-colors"
            >
                ✕
            </button>
        </div>
    );
}

// Toast Container
function ToastContainer({ toasts, onRemove }) {
    return (
        <div className="fixed bottom-4 right-4 z-[9999] flex flex-col gap-2">
            {toasts.map((toast) => (
                <Toast key={toast.id} toast={toast} onRemove={onRemove} />
            ))}
        </div>
    );
}

// Toast Provider
export function ToastProvider({ children }) {
    const [toasts, setToasts] = useState([]);
    const timersRef = useRef({});

    // Clean up timers on unmount
    useEffect(() => {
        return () => {
            Object.values(timersRef.current).forEach(timer => clearTimeout(timer));
        };
    }, []);

    // Add toast
    const addToast = useCallback((message, type = ToastType.INFO, options = {}) => {
        const id = Date.now() + Math.random().toString(36).substr(2, 9);
        const duration = options.duration ?? (type === ToastType.ERROR ? 8000 : 5000);

        const toast = {
            id,
            message,
            type,
            title: options.title,
            createdAt: new Date(),
        };

        setToasts(prev => [...prev, toast]);

        // Auto remove after duration
        if (duration > 0) {
            timersRef.current[id] = setTimeout(() => {
                removeToast(id);
            }, duration);
        }

        return id;
    }, []);

    // Remove toast
    const removeToast = useCallback((id) => {
        setToasts(prev => prev.filter(t => t.id !== id));
        if (timersRef.current[id]) {
            clearTimeout(timersRef.current[id]);
            delete timersRef.current[id];
        }
    }, []);

    // Convenience methods
    const success = useCallback((message, options = {}) => {
        return addToast(message, ToastType.SUCCESS, options);
    }, [addToast]);

    const error = useCallback((message, options = {}) => {
        return addToast(message, ToastType.ERROR, options);
    }, [addToast]);

    const warning = useCallback((message, options = {}) => {
        return addToast(message, ToastType.WARNING, options);
    }, [addToast]);

    const info = useCallback((message, options = {}) => {
        return addToast(message, ToastType.INFO, options);
    }, [addToast]);

    const value = {
        toasts,
        addToast,
        removeToast,
        success,
        error,
        warning,
        info,
    };

    return (
        <ToastContext.Provider value={value}>
            {children}
            <ToastContainer toasts={toasts} onRemove={removeToast} />
        </ToastContext.Provider>
    );
}

// Hook to use toast
export function useToast() {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
}

export default ToastContext;
